import {
  Clapperboard,
  ImageIcon,
  Images,
  Library,
  MapPin,
  Package,
  Upload,
  Users,
  Layers,
  Loader2,
  Video,
  Volume2,
  Waypoints,
} from "lucide-react"
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { PostProductionIcon } from "@/components/icons/post-production-icon"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import {
  FLOW_NODE_TITLE_MAX_LENGTH,
  flowNodeCustomTitle,
  flowNodeEntityKind,
  flowNodePending,
  type FlowNodeKind,
  type FlowNodeRow,
} from "@/types/flow"

export const FLOW_NODE_KIND_ICON: Record<FlowNodeKind, typeof ImageIcon> = {
  upload: Upload,
  library: Library,
  image: ImageIcon,
  video: Video,
  video_transition: Images,
  audio: Volume2,
  video_upscale: PostProductionIcon,
  video_depth: Layers,
  chain: Waypoints,
  shot: Clapperboard,
  character: Users,
  location: MapPin,
  prop: Package,
}

type KindLabelKey =
  | "node.kinds.upload"
  | "node.kinds.library"
  | "node.kinds.image"
  | "node.kinds.video"
  | "node.kinds.video_transition"
  | "node.kinds.audio"
  | "node.kinds.video_upscale"
  | "node.kinds.video_depth"
  | "node.kinds.chain"
  | "node.kinds.shot"
  | "node.kinds.character"
  | "node.kinds.location"
  | "node.kinds.prop"

const KIND_LABEL_KEY: Record<FlowNodeKind, KindLabelKey> = {
  upload: "node.kinds.upload",
  library: "node.kinds.library",
  image: "node.kinds.image",
  video: "node.kinds.video",
  video_transition: "node.kinds.video_transition",
  audio: "node.kinds.audio",
  video_upscale: "node.kinds.video_upscale",
  video_depth: "node.kinds.video_depth",
  chain: "node.kinds.chain",
  shot: "node.kinds.shot",
  character: "node.kinds.character",
  location: "node.kinds.location",
  prop: "node.kinds.prop",
}

export function flowNodeKindIcon(kind: string): typeof ImageIcon {
  return FLOW_NODE_KIND_ICON[kind as FlowNodeKind] ?? ImageIcon
}

export function flowNodeKindLabelKey(kind: FlowNodeKind): KindLabelKey | null {
  return Object.prototype.hasOwnProperty.call(KIND_LABEL_KEY, kind) ? KIND_LABEL_KEY[kind] : null
}

type FlowNodeTitleContextValue = {
  canEdit: boolean
  renameTitle: (nodeId: number, title: string | null) => void
  chainInputs: boolean
  setChainInput: (nodeId: number, value: boolean) => void
}

const FlowNodeTitleContext = createContext<FlowNodeTitleContextValue | null>(null)

function noopSetChainInput(_nodeId: number, _value: boolean) {}

export function FlowNodeTitleProvider({
  canEdit,
  renameTitle,
  chainInputs = false,
  setChainInput = noopSetChainInput,
  children,
}: {
  canEdit: boolean
  renameTitle: (nodeId: number, title: string | null) => void
  chainInputs?: boolean
  setChainInput?: (nodeId: number, value: boolean) => void
  children: ReactNode
}) {
  const value = useMemo(
    () => ({ canEdit, renameTitle, chainInputs, setChainInput }),
    [canEdit, renameTitle, chainInputs, setChainInput],
  )
  return <FlowNodeTitleContext.Provider value={value}>{children}</FlowNodeTitleContext.Provider>
}

export function useFlowNodeTitle() {
  return useContext(FlowNodeTitleContext)
}

function displayedName(node: FlowNodeRow): string | null {
  if (flowNodeCustomTitle(node.kind)) return node.title?.trim() || null
  if (node.kind === "shot") return node.shot_label
  if (flowNodeEntityKind(node.kind)) return node.entity_name
  return null
}

export function FlowNodeTitle({ node }: { node: FlowNodeRow }) {
  const { t } = useTranslation("flow")
  const edit = useContext(FlowNodeTitleContext)
  const editable = flowNodeCustomTitle(node.kind) && Boolean(edit?.canEdit)
  const name = displayedName(node)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(node.title ?? "")
  const inputRef = useRef<HTMLInputElement>(null)
  const closedRef = useRef(false)
  const commitRef = useRef<(raw: string) => void>(() => {})
  const Icon = flowNodeKindIcon(node.kind)
  const kindLabelKey = flowNodeKindLabelKey(node.kind)
  const kindLabel = kindLabelKey ? t(kindLabelKey) : node.kind
  const showName = editable || Boolean(name)
  const fullName = name ?? (editable ? t("node.untitled") : "")

  useEffect(() => {
    if (!editing) setDraft(node.title ?? "")
  }, [editing, node.title])

  useEffect(() => {
    if (!editing) return
    const input = inputRef.current
    if (!input) return
    input.focus()
    const placeCaretAtEnd = () => {
      const end = input.value.length
      input.setSelectionRange(end, end)
    }
    placeCaretAtEnd()
    const frame = requestAnimationFrame(placeCaretAtEnd)
    return () => cancelAnimationFrame(frame)
  }, [editing])

  const beginEdit = () => {
    closedRef.current = false
    setDraft(node.title ?? "")
    setEditing(true)
  }

  const commitValue = (raw: string) => {
    if (closedRef.current) return
    closedRef.current = true
    setEditing(false)
    if (!edit) return
    const next = raw.trim() || null
    const current = node.title?.trim() || null
    if (next === current) return
    edit.renameTitle(node.id, next)
  }

  const cancelEdit = () => {
    if (closedRef.current) return
    closedRef.current = true
    setDraft(node.title ?? "")
    setEditing(false)
  }

  commitRef.current = commitValue

  useEffect(() => {
    if (!editing) return
    const onPointerDown = (event: PointerEvent) => {
      const input = inputRef.current
      if (!input) return
      if (event.target instanceof Node && input.contains(event.target)) return
      commitRef.current(input.value)
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    return () => document.removeEventListener("pointerdown", onPointerDown, true)
  }, [editing])

  return (
    <div
      className={cn(
        "absolute bottom-full left-0 z-10 mb-1 flex h-4 max-w-full items-center gap-1 overflow-hidden text-xs leading-4 text-ui-text-muted",
        editing ? "w-full" : "w-max",
        editable ? "nodrag nopan nowheel" : undefined,
        editable && !editing ? "cursor-text" : undefined,
      )}
      title={showName ? `${kindLabel} · ${fullName}` : kindLabel}
      role={editable && !editing ? "button" : undefined}
      tabIndex={editable && !editing ? 0 : undefined}
      onClick={() => {
        if (!editable || editing) return
        beginEdit()
      }}
      onKeyDown={(event) => {
        if (!editable || editing) return
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        beginEdit()
      }}
    >
      {node.batch_waiting && !flowNodePending(node) ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : null}
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="shrink-0">{kindLabel}</span>
      {showName ? (
        <>
          <span className="shrink-0" aria-hidden>
            ·
          </span>
          {editing ? (
            <input
              ref={inputRef}
              maxLength={FLOW_NODE_TITLE_MAX_LENGTH}
              value={draft}
              aria-label={t("node.rename")}
              className="nodrag nopan nowheel m-0 h-4 max-h-4 min-h-4 min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-xs leading-4 text-ui-text-primary outline-none select-text focus:ring-0"
              onChange={(event) => setDraft(event.target.value)}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  commitValue(event.currentTarget.value)
                } else if (event.key === "Escape") {
                  event.preventDefault()
                  cancelEdit()
                }
              }}
              onBlur={(event) => commitValue(event.currentTarget.value)}
            />
          ) : (
            <span className={cn("min-w-0 truncate", name ? "text-ui-text-secondary" : undefined)} title={fullName}>
              {fullName}
            </span>
          )}
        </>
      ) : null}
    </div>
  )
}
